import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShieldAlt, faEnvelope, faLock, faArrowRight, faSpinner, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { API_BASE_URL } from "../../config/api";
import "../css/style.css";

function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Please enter both email and password.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admins/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });

      const data = await res.json();

      if (res.ok) {
        localStorage.setItem("adminId", data.id);
        localStorage.setItem("adminName", data.username);
        localStorage.setItem("adminEmail", data.email);
        localStorage.setItem("adminRole", data.role);
        navigate("/dashboard");
      } else {
        setError(data.error || "Invalid credentials.");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("Unable to connect to server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #312e81 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        position: "relative",
        overflow: "hidden"
      }}
    >
      {/* Background Orbs */}
      <div style={{ position: "absolute", width: "400px", height: "400px", borderRadius: "50%", background: "rgba(79,70,229,0.08)", top: "-100px", left: "-100px", filter: "blur(80px)" }} />
      <div style={{ position: "absolute", width: "350px", height: "350px", borderRadius: "50%", background: "rgba(99,102,241,0.06)", bottom: "-80px", right: "-80px", filter: "blur(80px)" }} />

      <div
        style={{
          background: "rgba(255,255,255,0.04)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "24px",
          padding: "48px 40px",
          width: "100%",
          maxWidth: "440px",
          boxShadow: "0 24px 48px rgba(0,0,0,0.3)",
          animation: "fadeIn 0.5s ease-out"
        }}
      >
        {/* Logo & Header */}
        <div style={{ textAlign: "center", marginBottom: "36px" }}>
          <div
            style={{
              width: "72px",
              height: "72px",
              borderRadius: "20px",
              background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
              boxShadow: "0 8px 24px rgba(79,70,229,0.4)"
            }}
          >
            <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: "1.8rem", color: "#fff" }} />
          </div>
          <h1
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 800,
              fontSize: "1.6rem",
              color: "#ffffff",
              margin: "0 0 6px",
              letterSpacing: "-0.02em"
            }}
          >
            Admin Portal
          </h1>
          <p style={{ color: "#94a3b8", fontSize: "0.9rem", margin: 0 }}>
            Consortium E-Learning Management Dashboard
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div
            style={{
              background: "rgba(239,68,68,0.1)",
              border: "1px solid rgba(239,68,68,0.2)",
              borderRadius: "12px",
              padding: "12px 16px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              gap: "10px"
            }}
          >
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: "#ef4444", fontSize: "0.9rem" }} />
            <span style={{ color: "#fca5a5", fontSize: "0.88rem", fontWeight: 600 }}>{error}</span>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleLogin} style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
          <div>
            <label style={{ color: "#94a3b8", fontSize: "0.82rem", fontWeight: 700, display: "block", marginBottom: "8px" }}>
              Admin Email Address
            </label>
            <div style={{ position: "relative" }}>
              <FontAwesomeIcon
                icon={faEnvelope}
                style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "#64748b", fontSize: "0.9rem" }}
              />
              <input
                type="email"
                placeholder="admin@consortium.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{
                  width: "100%",
                  padding: "14px 16px 14px 44px",
                  borderRadius: "12px",
                  border: "1.5px solid rgba(255,255,255,0.1)",
                  background: "rgba(255,255,255,0.04)",
                  color: "#e2e8f0",
                  fontSize: "0.92rem",
                  fontWeight: 600,
                  outline: "none",
                  transition: "border-color 0.2s ease",
                  boxSizing: "border-box"
                }}
                onFocus={(e) => (e.target.style.borderColor = "#4f46e5")}
                onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.1)")}
              />
            </div>
          </div>

          <div>
            <label style={{ color: "#94a3b8", fontSize: "0.82rem", fontWeight: 700, display: "block", marginBottom: "8px" }}>
              Password
            </label>
            <div style={{ position: "relative" }}>
              <FontAwesomeIcon
                icon={faLock}
                style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "#64748b", fontSize: "0.9rem" }}
              />
              <input
                type="password"
                placeholder="Enter your admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: "100%",
                  padding: "14px 16px 14px 44px",
                  borderRadius: "12px",
                  border: "1.5px solid rgba(255,255,255,0.1)",
                  background: "rgba(255,255,255,0.04)",
                  color: "#e2e8f0",
                  fontSize: "0.92rem",
                  fontWeight: 600,
                  outline: "none",
                  transition: "border-color 0.2s ease",
                  boxSizing: "border-box"
                }}
                onFocus={(e) => (e.target.style.borderColor = "#4f46e5")}
                onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.1)")}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
              color: "#fff",
              border: "none",
              padding: "15px",
              borderRadius: "12px",
              fontWeight: 800,
              fontSize: "1rem",
              cursor: loading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              boxShadow: "0 6px 20px rgba(79,70,229,0.4)",
              transition: "all 0.25s ease",
              marginTop: "4px",
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Authenticating...
              </>
            ) : (
              <>
                Sign In to Dashboard <FontAwesomeIcon icon={faArrowRight} />
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <div style={{ textAlign: "center", marginTop: "28px" }}>
          <p style={{ color: "#475569", fontSize: "0.8rem", margin: 0 }}>
            Restricted access for authorized administrators only.
          </p>
        </div>
      </div>
    </div>
  );
}

export default AdminLogin;
